import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import App from './App.tsx'
import './index.css'
import { registerDevice } from './services/deviceRegistration'
import { loadRuntimeConfig } from './services/apiConfig'
import { installEnglishDigitGuard } from './utils/englishDigits'
import './utils/dateConverter'
import { installPrintPageNumbers } from './utils/printUtils'

installPrintPageNumbers()



// Initialize theme from localStorage on app load
const initializeTheme = () => {
  try {
    const savedTheme = localStorage.getItem('theme-storage')
    if (savedTheme) {
      const parsed = JSON.parse(savedTheme)
      if (parsed.state?.theme) {
        document.documentElement.setAttribute('data-theme', parsed.state.theme)
        return
      }
    }
  } catch (error) {
    console.error('Error loading theme:', error)
  }
  // Default to normal theme
  document.documentElement.setAttribute('data-theme', 'normal')
}

// Initialize font settings from localStorage on app load
const initializeFont = () => {
  try {
    const savedFont = localStorage.getItem('font-storage')
    if (savedFont) {
      const parsed = JSON.parse(savedFont)
      if (parsed.state) {
        const fontFamilyMap: Record<string, string> = {
          'Cairo': "'Cairo', Arial, sans-serif",
          'Arial': "Arial, sans-serif",
          'Tahoma': "Tahoma, Arial, sans-serif",
          'Segoe UI': "'Segoe UI', Tahoma, Arial, sans-serif",
          'Roboto': "'Roboto', Arial, sans-serif",
          'Amiri': "'Amiri', 'Cairo', Arial, serif",
          'Lateef': "'Lateef', 'Cairo', Arial, sans-serif",
          'Tajawal': "'Tajawal', 'Cairo', Arial, sans-serif",
          'Changa': "'Changa', 'Cairo', Arial, sans-serif",
          'El Messiri': "'El Messiri', 'Cairo', Arial, sans-serif",
          'Reem Kufi': "'Reem Kufi', 'Cairo', Arial, sans-serif",
          'Almarai': "'Almarai', 'Cairo', Arial, sans-serif",
          'Noto Sans Arabic': "'Noto Sans Arabic', 'Cairo', Arial, sans-serif",
          'Noto Naskh Arabic': "'Noto Naskh Arabic', 'Cairo', Arial, sans-serif",
          'IBM Plex Sans Arabic': "'IBM Plex Sans Arabic', 'Cairo', Arial, sans-serif",
          'Rubik': "'Rubik', 'Cairo', Arial, sans-serif",
          'Scheherazade New': "'Scheherazade New', 'Cairo', Arial, serif",
          'Harmattan': "'Harmattan', 'Cairo', Arial, sans-serif",
          'Markazi Text': "'Markazi Text', 'Cairo', Arial, serif",
        }
        const fontSizeMap: Record<string, string> = {
          'small': '12px',
          'medium': '14px',
          'large': '16px',
          'xlarge': '18px',
        }
        
        if (parsed.state.fontFamily && fontFamilyMap[parsed.state.fontFamily]) {
          const css = fontFamilyMap[parsed.state.fontFamily]
          document.documentElement.style.setProperty('--font-family', css)
          document.documentElement.style.fontFamily = css
          document.body.style.fontFamily = css
        }
        
        if (parsed.state.fontSize && fontSizeMap[parsed.state.fontSize]) {
          document.documentElement.style.setProperty('--font-size-base', fontSizeMap[parsed.state.fontSize])
          document.body.style.fontSize = fontSizeMap[parsed.state.fontSize]
        }
        return
      }
    }
  } catch (error) {
    console.error('Error loading font settings:', error)
  }
  // Default font settings (Cairo — same as before)
  const defaultFont = "'Cairo', Arial, sans-serif"
  document.documentElement.style.setProperty('--font-family', defaultFont)
  document.documentElement.style.setProperty('--font-size-base', '14px')
  document.documentElement.style.fontFamily = defaultFont
  document.body.style.fontFamily = defaultFont
  document.body.style.fontSize = '14px'
}

async function bootstrap() {
  // تحميل ملف التكوين الخارجي قبل بدء التطبيق (إن وجد)
  await loadRuntimeConfig()

  // Initialize theme and font before React renders
  initializeTheme()
  initializeFont()
  installEnglishDigitGuard()

  // Register device on first launch (will be handled in App component to show dialog if needed)
  // registerDevice().catch(console.error)

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        retry: 0,
      },
      mutations: {
        retry: 0,
      },
    },
  })

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
          <Toaster
            position="top-center"
            toastOptions={{
              error: {
                duration: 8000,
                style: { whiteSpace: 'pre-line', maxWidth: 520 },
              },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

bootstrap()




